import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { depositText, DepositText } from '../i18n/pages/deposit';
import {
  DepositClaimReason,
  PublicDepositStatus,
  getPublicDeposit,
  startDepositCardSetup,
} from '../api.deposit';

interface Props {
  formToken: string;
  /**
   * `guide` renders inside the open check-in guide, where the deposit is one section among many.
   * `closed` renders after the guide has expired, where it is the only thing on the page — money
   * can still be held for days after a guest has gone home.
   */
  variant: 'guide' | 'closed';
}

const sentence = (reason: DepositClaimReason | null, t: DepositText): string => {
  if (!reason) return '';
  return {
    DAMAGE: t.gDamage, MISSING_ITEMS: t.gMissing, EXTRA_CLEANING: t.gCleaning,
    SMOKING: t.gSmoking, EXTRA_GUESTS: t.gGuests, LATE_CHECKOUT: t.gLate, OTHER: t.gOther,
  }[reason];
};

/**
 * The deposit as the guest sees it, in the guide and after it has closed.
 *
 * <p>The wording never says "nothing happens". A hold genuinely makes the money unavailable — it
 * shows as pending in their banking app — so the copy says the amount is *set aside*, and every
 * release message says the bank may take days to drop it. Without that line, "I haven't got my
 * money back" is the most common message this feature will ever generate.
 */
const DepositGuestSection: React.FC<Props> = ({ formToken, variant }) => {
  const { lang } = useLang();
  const t = depositText[lang];

  const [status, setStatus] = useState<PublicDepositStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await getPublicDeposit(formToken));
    } catch {
      setStatus(null);
    }
  }, [formToken]);

  useEffect(() => { void load(); }, [load]);

  if (!status?.required) return null;

  const money = (v: number | null) =>
    v === null ? '' : `${v.toLocaleString(lang === 'en' ? 'en-GB' : lang)} ${status.currency}`;
  const fill = (s: string, v: Record<string, string>) =>
    Object.entries(v).reduce((a, [k, x]) => a.replace(`{${k}}`, x), s);
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');

  const toStripe = async () => {
    setBusy(true);
    setError(null);
    try {
      window.location.href = await startDepositCardSetup(formToken);
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
      setBusy(false);
    }
  };

  const kept = status.capturedAmount;
  const anythingKept = kept !== null && kept > 0;

  // ── After the decision ───────────────────────────────────────────────────
  if (status.status === 'CLAIMED' || status.status === 'RELEASED' || status.status === 'EXPIRED') {
    return (
      <div style={{ ...styles.card, ...(anythingKept ? styles.red : styles.green) }}>
        <div style={styles.title}>
          🔒 {anythingKept ? t.guestKeptTitle : t.guestReleasedTitle}
        </div>
        <div style={styles.body}>
          {anythingKept
            ? <>
                {fill(t.guestKeptBody, {
                  amount: money(kept),
                  total: money(status.amount),
                  rest: money((status.amount ?? 0) - (kept ?? 0)),
                })}{' '}{sentence(status.claimReason, t)}
              </>
            : fill(t.guestReleasedBody, { date: day(status.decidedAt) })}
        </div>
        <div style={styles.small}>{t.guestBankDelay}</div>
        {anythingKept && <div style={styles.small}>{t.guestAskHost}</div>}
      </div>
    );
  }

  // ── Money reserved, waiting on the host ──────────────────────────────────
  if (status.status === 'HELD') {
    return (
      <div style={{ ...styles.card, ...styles.blue }}>
        <div style={styles.title}>🔒 {t.guestHeldTitle}</div>
        <div style={styles.amount}>{money(status.amount)}</div>
        <div style={styles.body}>
          {fill(t.guestHeldBody, { date: day(status.expectedBackBy) })}
        </div>
        <div style={styles.small}>{t.guestBankDelay}</div>
      </div>
    );
  }

  // ── Still needs a card, or the bank refused ──────────────────────────────
  // Two different messages on purpose. "We need a card" is a step they have not taken; "we couldn't
  // reserve it" is one they took that failed. Rendering both as an absence is how a guest ends up
  // waiting patiently for a code that is never coming.
  const failed = status.status === 'HOLD_FAILED';

  return (
    <div style={{ ...styles.card, ...styles.amber }}>
      <div style={styles.title}>
        {failed ? '⚠' : '🔒'}{' '}
        {failed
          ? fill(t.guestFailedTitle, { amount: money(status.amount) })
          : fill(t.guestNeedCardTitle, { amount: money(status.amount) })}
      </div>
      <div style={styles.body}>
        {failed ? t.guestFailedBody : t.guestNeedCardBody}
      </div>
      <button type="button" style={styles.cta} disabled={busy} onClick={toStripe}>
        {failed ? t.guestUpdateCard : t.guestAddCard}
      </button>
      {error && <div style={styles.error}>{error}</div>}
      {variant === 'closed' && <div style={styles.small}>{t.guestAskHost}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex', flexDirection: 'column', gap: 6,
    borderRadius: 11, padding: '14px 16px', marginBottom: 16,
  },
  amber: { background: '#fffbeb', border: '1px solid #fcd34d' },
  blue: { background: '#eff6ff', border: '1px solid #bfdbfe' },
  green: { background: '#f0fdf4', border: '1px solid #bbf7d0' },
  red: { background: '#fef2f2', border: '1px solid #fecaca' },
  title: { fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', lineHeight: 1.4 },
  amount: { fontSize: '1.7rem', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 },
  body: { fontSize: '0.84rem', color: '#1e293b', lineHeight: 1.55 },
  small: { fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 },
  cta: {
    alignSelf: 'flex-start', marginTop: 6, fontSize: '0.86rem', fontWeight: 700,
    padding: '9px 18px', borderRadius: 8, border: 0,
    background: '#b45309', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  error: {
    marginTop: 6, padding: '8px 10px', borderRadius: 8,
    background: '#fef2f2', color: '#b91c1c', fontSize: '0.78rem',
  },
};

export default DepositGuestSection;
